// Fila de envio com persistencia.
//
// O quarto do hospital nao tem rede confiavel, e uma medida perdida e uma medida
// que ninguem mais vai recuperar: o aparelho ja mediu, a fita ja foi usada, o
// dedo ja foi furado. Entao toda leitura entra na fila ANTES de tentar a rede,
// e so sai de la quando o servidor confirma.
//
// O armazenamento fica atras de uma interface para a fila poder ser testada sem
// navegador — IndexedDB nao existe no Node.

import type { LeituraParaEnviar } from '../ble/traduzir';

export interface ItemDaFila {
  /** Chave natural: mesma medida reenviada nao vira duas. */
  id: string;
  leitura: LeituraParaEnviar;
  criadoEm: number;
  tentativas: number;
  ultimoErro?: string;
}

export interface Armazenamento {
  listar(): Promise<ItemDaFila[]>;
  guardar(item: ItemDaFila): Promise<void>;
  remover(id: string): Promise<void>;
}

/**
 * Identidade da medida, para o mesmo quadro lido duas vezes ocupar uma vaga só.
 *
 * Quando o aparelho informa o próprio carimbo de tempo, é ELE que identifica a
 * medida — não o instante em que chegou. O LinkSun entrega o mesmo quadro mais
 * de uma vez, com milissegundos de diferença: comparando pelo recebimento, a
 * mesma glicemia entrava duas vezes no histórico (visto em 16/09/2026, dois
 * registros de 184 mg/dL no mesmo segundo).
 *
 * O relógio do aparelho está errado, mas para IDENTIFICAR serve — o que não
 * serve é para datar, e é por isso que ele não vira `medidoEm`.
 */
export function chaveDaLeitura(pacienteId: string, l: LeituraParaEnviar): string {
  const identidade = l.medidoEmAparelho ?? l.medidoEm;
  return `${pacienteId}|${l.tipo}|${identidade}|${l.origem}`;
}

export class ArmazenamentoEmMemoria implements Armazenamento {
  private itens = new Map<string, ItemDaFila>();
  async listar() { return [...this.itens.values()].sort((a, b) => a.criadoEm - b.criadoEm); }
  async guardar(item: ItemDaFila) { this.itens.set(item.id, item); }
  async remover(id: string) { this.itens.delete(id); }
}

/** IndexedDB: sobrevive a recarregar a pagina e a reiniciar o tablet. */
export class ArmazenamentoIndexedDB implements Armazenamento {
  private banco: Promise<IDBDatabase>;

  constructor(nome = 'medicov1', private loja = 'fila') {
    this.banco = new Promise((ok, falhou) => {
      const req = indexedDB.open(nome, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(this.loja)) {
          req.result.createObjectStore(this.loja, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => ok(req.result);
      req.onerror = () => falhou(req.error);
    });
  }

  private async transacao<T>(modo: IDBTransactionMode, op: (l: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const banco = await this.banco;
    return new Promise((ok, falhou) => {
      const req = op(banco.transaction(this.loja, modo).objectStore(this.loja));
      req.onsuccess = () => ok(req.result);
      req.onerror = () => falhou(req.error);
    });
  }

  async listar() {
    const itens = await this.transacao<ItemDaFila[]>('readonly', (l) => l.getAll());
    return itens.sort((a, b) => a.criadoEm - b.criadoEm);
  }
  async guardar(item: ItemDaFila) { await this.transacao('readwrite', (l) => l.put(item)); }
  async remover(id: string) { await this.transacao('readwrite', (l) => l.delete(id)); }
}

export type ResultadoEnvio = 'enviado' | 'recusado' | 'sem_rede';

/** Quanto esperar antes de tentar de novo, subindo até 5 min. */
export function esperaAntesDeTentar(tentativas: number): number {
  return Math.min(1000 * 2 ** tentativas, 300_000);
}

/** Depois disto a leitura para de ser reenviada e fica marcada para revisao. */
export const TENTATIVAS_ATE_DESISTIR = 12;

export class FilaDeEnvio {
  constructor(
    private armazenamento: Armazenamento,
    private enviar: (leitura: LeituraParaEnviar) => Promise<ResultadoEnvio>,
  ) {}

  async enfileirar(pacienteId: string, leitura: LeituraParaEnviar): Promise<void> {
    await this.armazenamento.guardar({
      id: chaveDaLeitura(pacienteId, leitura),
      leitura,
      criadoEm: Date.now(),
      tentativas: 0,
    });
  }

  async pendentes(): Promise<ItemDaFila[]> {
    return this.armazenamento.listar();
  }

  /**
   * Tenta esvaziar a fila. Erro de rede mantem o item; recusa do servidor
   * (dado invalido) descarta, porque retentar nao muda nada e a fila travaria
   * para sempre atras de um item impossivel.
   */
  async escoar(agora = Date.now()): Promise<{ enviados: number; mantidos: number; descartados: number }> {
    let enviados = 0, mantidos = 0, descartados = 0;

    for (const item of await this.armazenamento.listar()) {
      if (agora - item.criadoEm < esperaAntesDeTentar(item.tentativas) && item.tentativas > 0) {
        mantidos++;
        continue;
      }

      let resultado: ResultadoEnvio;
      try {
        resultado = await this.enviar(item.leitura);
      } catch (e) {
        resultado = 'sem_rede';
        item.ultimoErro = e instanceof Error ? e.message : String(e);
      }

      if (resultado === 'enviado' || resultado === 'recusado') {
        await this.armazenamento.remover(item.id);
        if (resultado === 'enviado') enviados++; else descartados++;
        continue;
      }

      const tentativas = item.tentativas + 1;
      if (tentativas >= TENTATIVAS_ATE_DESISTIR) {
        // Nao apagamos: a medida existiu. Fica na fila para alguem ver.
        await this.armazenamento.guardar({ ...item, tentativas, ultimoErro: 'desistiu de reenviar' });
      } else {
        await this.armazenamento.guardar({ ...item, tentativas });
      }
      mantidos++;
    }

    return { enviados, mantidos, descartados };
  }
}
